"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.adjustContentStyles = void 0;
var _tinycolor = _interopRequireDefault(require("tinycolor2"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Function to identify if any part of content has white or black backgrounds then make it transparant
 * If other background, then make text color contrast to its background
 * If no background and text color is close to black or white then make it default text color
 * @param content string
 * @returns modified content with adjusted styles
 */
const adjustContentStyles = content => {
  if (!content) return content;
  let updatedContent = content.replace(/\n/g, ' ');
  updatedContent = updatedContent.replace(/style="([^"]*?)"/gi, (match, styles) => {
    let updatedStyles = styles;

    /**
     * Extract Background color and text color if applied
     */
    const bgColorMatch = styles.match(/background-color:\s*([^;]+)/i);
    const backgroundMatch = styles.match(/background:\s*([^;]+)/i);
    const colorMatch = styles.match(/color:\s*([^;]+)/i);
    let backgroundColorExists = false;
    let contrastColor = '';

    /**
     * If there is background-color applied
     * 1. Check if the color is close to white or black
     * 2. If Yes, make it transparant
     * 3. If not, keep the background color as is and get its contrast color to update the text color if needed so that text is visible on this background
     */
    if (bgColorMatch && bgColorMatch.length > 1) {
      const originalColor = bgColorMatch[1].trim();
      if (originalColor !== 'transparent') {
        const isBlackOrWhite = isCloseToBlackOrWhite(originalColor);
        if (isBlackOrWhite) {
          updatedStyles = updatedStyles.replace(bgColorMatch[0], `background-color:transparent`);
        } else {
          backgroundColorExists = true;
          const col = (0, _tinycolor.default)(originalColor)?.toHexString();
          if (col) contrastColor = getContrastText(col);
        }
      }
    }

    /**
     * If there is background applied
     * 1. Check if the color is close to white or black
     * 2. If Yes, make it transparant
     * 3. If not, keep the background color as is and get its contrast color to update the text color if needed so that text is visible on this background
     */
    if (backgroundMatch && backgroundMatch.length > 1) {
      const originalColor = backgroundMatch[1].trim();
      if (originalColor !== 'transparent') {
        const isBlackOrWhite = isCloseToBlackOrWhite(originalColor);
        if (isBlackOrWhite) {
          updatedStyles = updatedStyles.replace(backgroundMatch[0], `background:transparent`);
        } else {
          backgroundColorExists = true;
          const col = (0, _tinycolor.default)(originalColor)?.toHexString();
          if (col) contrastColor = getContrastText(col);
        }
      }
    }

    /**
     * If background still kept intack without changing it to transparant,
     * 1. Check if text has color
     * 2. If text has no color, then we need to update the text color with contrast color of background because text color will be changed as per theme mode but we dont want the text color to change cause in one of the themes, the text color may not be readable on the existing text background color
     * 3. If yes, then dont change color as user intentionally kept this color for text for the given background
     *
     * If background color removed or doesn't exist at all and text has some color to it
     * 1. Check if text color is close to black or white
     * 2. If yes, make it inherit so that it will inherit based on current theme
     */
    if (backgroundColorExists) {
      if (!colorMatch && contrastColor) {
        updatedStyles += `color:${contrastColor};`;
      }
    } else if (colorMatch && colorMatch.length > 1) {
      const originalColor = colorMatch[1].trim();
      if (originalColor !== 'inherit') {
        const isBlackOrWhite = isCloseToBlackOrWhite(originalColor);
        if (isBlackOrWhite) updatedStyles = updatedStyles.replace(colorMatch[0], `color:inherit`);
      }
    }
    return `style="${updatedStyles}"`;
  });
  updatedContent = updatedContent.replace(/&nbsp;/g, '');
  return updatedContent;
};

/**
 * Getting the luminance based on a formula recommended by WCAG guidelines
 * @param color string
 * @returns luminance value number
 */
exports.adjustContentStyles = adjustContentStyles;
const getPerceivedLuminance = color => {
  let tc = (0, _tinycolor.default)(color);
  if (!tc?.isValid()) return 0;
  let {
    r,
    g,
    b
  } = tc.toRgb();
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

/**
 * Tested with multiple color codes and came up with 25 as threshold value to decide if it very close to black or white
 * @param color string
 * @returns boolean
 */
const isCloseToBlackOrWhite = color => {
  const luminance = getPerceivedLuminance(color);
  return luminance < 25 || luminance > 230;
};

/**
 * If luminance is greater than half of 255 then color considered close to white as per WCAG
 * So if luminance > 123 then contrast is black
 * otherwise white
 * @param color string
 * @returns contrast color code
 */
const getContrastText = color => {
  const luminance = getPerceivedLuminance(color);
  if (luminance > 123) return '#24292E';
  return '#ffffff';
};